import React from 'react';
import {Pressable, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {AnalysisCell, AnalysisLine, analysisRows} from '../analysisLine';
import {F} from '../courseTheme';

type Props = {
  line: AnalysisLine;
  /** Show the position after ply `index` (0 is the start). */
  onGo: (index: number) => void;
  /**
   * `sheet`: a row per move number, as the student's scoresheet. `inline`:
   * one wrapping line, for the editor, where the board gets the height.
   */
  layout?: 'sheet' | 'inline';
  /** Beside the step buttons — the editor's way in and out of exploring. */
  action?: React.ReactNode;
};

/** The move on the board: filled, so it reads without colour alone. */
const CURRENT_BG = F.tealDark;
/** A move tried in analysis rather than played in the puzzle. */
const EXPLORED_FG = '#806234';

/**
 * The line being analysed as a scoresheet whose moves are buttons: pressing
 * one puts its position on the board, and the engine follows. The start and a
 * step either way sit above it, since "one move back" is the commonest jump.
 */
export default function AnalysisNotation({line, onGo, layout = 'sheet', action}: Props) {
  const rows = analysisRows(line);
  const atStart = line.index === 0;
  const atEnd = line.index >= line.fens.length - 1;
  return (
    <View style={{gap: 6}}>
      <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
        <CCText
          style={{fontSize: 12, fontWeight: '700', color: F.mutedText, letterSpacing: 0.6, flex: 1}}>
          LINE
        </CCText>
        {action}
        <StepButton
          icon="play-skip-back"
          label="Go to the start"
          disabled={atStart}
          selected={atStart}
          onPress={() => onGo(0)}
        />
        <StepButton
          icon="chevron-back"
          label="Previous move"
          disabled={atStart}
          onPress={() => onGo(line.index - 1)}
        />
        <StepButton
          icon="chevron-forward"
          label="Next move"
          disabled={atEnd}
          onPress={() => onGo(line.index + 1)}
        />
      </View>
      {rows.length && layout === 'inline' ? (
        <View style={{flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 2}}>
          {rows.map(row => (
            <React.Fragment key={row.number}>
              <CCText
                style={{
                  fontSize: 13,
                  fontWeight: '600',
                  color: F.mutedText,
                  marginLeft: row.number > 1 ? 4 : 0,
                  fontVariant: ['tabular-nums'],
                }}>
                {row.white ? `${row.number}.` : `${row.number}…`}
              </CCText>
              {row.white ? (
                <MoveCell inline cell={row.white} label={c => `Go to ${row.number}. ${c.san}`} onGo={onGo} />
              ) : null}
              {row.black ? (
                <MoveCell inline cell={row.black} label={c => `Go to ${row.number}… ${c.san}`} onGo={onGo} />
              ) : null}
            </React.Fragment>
          ))}
        </View>
      ) : rows.length ? (
        <View style={{gap: 2, maxWidth: 380}}>
          {rows.map(row => (
            <View key={row.number} style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
              <CCText
                style={{
                  width: 30,
                  textAlign: 'right',
                  fontSize: 13,
                  fontWeight: '600',
                  color: F.mutedText,
                  fontVariant: ['tabular-nums'],
                }}>
                {row.white ? `${row.number}.` : `${row.number}…`}
              </CCText>
              <MoveCell cell={row.white} label={c => `Go to ${row.number}. ${c.san}`} onGo={onGo} />
              <MoveCell cell={row.black} label={c => `Go to ${row.number}… ${c.san}`} onGo={onGo} />
            </View>
          ))}
        </View>
      ) : (
        <CCText style={{fontSize: 13, fontWeight: '500', color: F.mutedText}}>
          Make a move on the board to start a line.
        </CCText>
      )}
    </View>
  );
}

function MoveCell({
  cell,
  label,
  onGo,
  inline = false,
}: {
  cell: AnalysisCell | null;
  label: (cell: AnalysisCell) => string;
  onGo: (index: number) => void;
  inline?: boolean;
}) {
  if (!cell) return <View style={{flex: 1}} />;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label(cell)}
      accessibilityState={{selected: cell.current}}
      onPress={() => onGo(cell.index)}
      style={({pressed, hovered, focused}: any) => ({
        ...(inline ? null : {flex: 1}),
        minHeight: 32,
        justifyContent: 'center',
        paddingHorizontal: 8,
        borderRadius: 6,
        backgroundColor: cell.current
          ? CURRENT_BG
          : pressed
          ? '#EFE5CC'
          : hovered
          ? F.badgeBg
          : 'transparent',
        cursor: 'pointer',
        ...(focused
          ? {outlineStyle: 'solid', outlineWidth: 2, outlineColor: F.tealDark, outlineOffset: 1}
          : null),
      })}>
      <CCText
        style={{
          fontSize: 14,
          fontWeight: cell.current ? '700' : '600',
          // White on tealDark is 5.2:1; the explored brown on white 5.9:1.
          color: cell.current ? F.white : cell.explored ? EXPLORED_FG : F.brownDark,
          fontStyle: cell.explored && !cell.current ? 'italic' : 'normal',
        }}>
        {cell.san}
      </CCText>
    </Pressable>
  );
}

function StepButton({
  icon,
  label,
  disabled,
  selected = false,
  onPress,
}: {
  icon: string;
  label: string;
  disabled: boolean;
  selected?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{disabled, selected}}
      disabled={disabled}
      onPress={onPress}
      hitSlop={6}
      style={({pressed, hovered, focused}: any) => ({
        width: 32,
        height: 32,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: F.border10,
        backgroundColor: pressed && !disabled ? '#EFE5CC' : hovered && !disabled ? F.badgeBg : F.white,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...(focused && !disabled
          ? {outlineStyle: 'solid', outlineWidth: 2, outlineColor: F.tealDark, outlineOffset: 1}
          : null),
      })}>
      <Ionicons name={icon} size={16} color={F.brown} />
    </Pressable>
  );
}
